import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {catchError, debounceTime, filter, map, mergeMap, switchMap} from 'rxjs/operators';
import {from, of, Observable} from 'rxjs';

import { FuseSplashScreenService } from '@barebone-fuse-services/splash-screen.service';
import {UserInfo} from '@shared/models/user-info.model';
import {SecurityState} from '@shared/store/reducers/security.reducer';
import {getUserInfo} from '@shared/store/selectors/security.selector';
import {StringUtility} from '@shared/utility/string-utility';
import {
  CHANGE_CONVERSATION_TITLE,
  ChangeConversationTitleAction,
  ChangeConversationTitleSuccessAction,
  CREATE_CHAT_CONVERSATION,
  CREATE_CONVERSATION,
  CREATE_CONVERSATION_COLLABORATION,
  CREATE_CONVERSATION_CONTEXT,
  CREATE_CONVERSATION_CONTEXT_WITH_RELATIVE_PATH,
  CREATE_CUSTOMER_SUPPORT_CHAT_CONVERSATION,
  CreateChatConversationAction,
  CreateChatConversationFailedAction,
  CreateConversationAction,
  CreateConversationContextAction,
  CreateConversationContextFailedAction,
  CreateConversationContextSuccessAction,
  CreateConversationContextWithRelativePathAction,
  CreateConversationContextWithRelativePathFailedAction,
  CreateConversationContextWithRelativePathSuccessAction,
  CreateConversationFailedAction,
  CreateCustomerSupportChatConversationAction,
  DELETE_CONVERSATION,
  DELETE_CONVERSATION_LIST,
  DELETE_CONVERSATION_MESSAGE,
  DeleteConversationAction,
  DeleteConversationFailedAction,
  DeleteConversationListAction,
  DeleteConversationListFailedAction,
  DeleteConversationMessageAction,
  DeleteConversationMessageFailedAction,
  DeleteConversationMessageSuccessAction,
  DeleteConversationSuccessAction,
  GET_COLLABORATION_AVATAR,
  GET_CONTEXT_BY_PATH,
  GET_CONTEXT_BY_PATH_SUCCESS,
  GET_CONVERSATION,
  GET_CONVERSATION_CHAT,
  GET_CONVERSATION_CHAT_SUCCESS,
  GET_CONVERSATION_LIST,
  GET_CONVERSATION_SUCCESS,
  GetCollaborationAvatarAction,
  GetCollaborationAvatarFailedAction,
  GetCollaborationAvatarSuccessAction,
  GetContextByPathAction,
  GetContextByPathFailedAction,
  GetContextByPathSuccessAction,
  GetConversationAction,
  GetConversationChatAction,
  GetConversationChatFailedAction,
  GetConversationChatSuccessAction,
  GetConversationFailedAction,
  GetConversationListAction,
  GetConversationListFailedAction,
  GetConversationListSuccessAction,
  GetConversationSuccessAction,
  IncreaseQueryProviderCallCounterFromCollaborationAction,
  LIVE_SUPPORT_JOINED_NOTIFICATION,
  MANAGE_PARTICIPANTS,
  OTHERS_MANAGE_PARTICIPANTS,
  ManageParticipantsAction,
  ManageParticipantsFailedAction,
  ManageParticipantsNotificationAction,
  ManageParticipantsSuccessAction,
  MESSAGE_READ,
  MessageReadAction,
  MessageReadFailedAction,
  MessageReadSuccessAction,
  NEW_MESSAGE_NOTIFICATION,
  OTHERS_CREATE_CONVERSATION,
  PICK_UP_LIVE_SUPPORT_CONVERSATION,
  PickupLiveSupportConversationAction,
  SEND_MESSAGE,
  SendMessageAction,
  SendMessageFailedAction,
  SEND_MESSAGE_CONFIRMED,
  SendMessageConfirmedAction,
  SEND_MESSAGE_SUCCESS,
  SendMessageSuccessAction,
  START_LIVE_SUPPORT_CONVERSATION,
  EDIT_CONVERSATION_MESSAGE,
  EditConversationMessageAction,
  EditConversationMessageSuccessAction,
  EditConversationMessageFailedAction,
  LEAVE_CONVERSATION,
  LeaveConversationAction,
  LeaveConversationFailedAction, OthersLeaveConversationNotificationAction, OTHERS_LEAVE_CONVERSATION,
  NewMessageNotificationAction, GET_CONTEXT_WITH_CONVERSATION_LIST_BY_PATH, GetContextWithConversationListByPathAction,
  GetContextWithConversationListByPathFailedAction, OthersCreateConversationNotificationAction
} from './collaboration.action';
import {CommandService} from '@core/services/command.service';
import {CollaborationApiService} from './collaboration-api.service';
import {GraphQLService} from '@core/services/graphql.service';
import {SnackBarService} from '@core/services/snack-bar.service';
import {DoNothingAction} from '@shared/store/actions/particle.actions';
import {CollaborationConfig} from './collaboration.config';
import {CollaborationQuery} from './collaboration.query';
import {
  ConversationComponentName, ConversationListComponentName, EditConversationListComponentName,
  FieldConversationComponentName, MessagesPageHost
} from './collaboration.constants';
import {SetCurrentRightSideComponentAction} from '@shared/components/platform-sidebar/store/platform-sidebar.action';
import {
  CreateChatConversationCommand, MessageReadCommand, SendMessageCommand,
  StartLiveSupportConversationCommand
} from './collaboration.command';
import {ContentScope} from '@shared/commands/command-base';
import {GraphqlContentScope} from '@shared/models/graphql-content-scope.model';
import {CollaborationState} from './collaboration.reducer';
import {Action, select, Store} from '@ngrx/store';
import {
  getConversationListInfo, getCurrentChatConversation,
  getLowestModifiedDateForThisConversationList
} from './collaboration.selector';
import {RightSidebarWaitScreenService} from "@core/services/right-sidebar-waitcreen.service";
import {ConversationListInfo} from "./collaboration.models";
import {refusalText} from '../messenger/message-windows';
import {listHolds} from '../messenger/chat-list-order';


@Injectable()
export class CollaborationEffects {
  public currentChatConversation: any;
  public lowestModifiedDateForThisConversationList: Date;
  public viewMore: boolean = true;
  public currentUser: UserInfo;
  public contextId: string;
  /** The loaded list's conversations, to tell a message for one it does not hold (ITP-E-240-04). */
  public listConversations: any[] = [];

  /** How long a burst of messages into chats the list does not hold is waited out before the list is read once. */
  static readonly listRereadDebounceMs = 300;

  constructor(private actions$: Actions,
              private commandService: CommandService,
              private collaborationService: CollaborationApiService,
              private fuseSplashScreenService: FuseSplashScreenService,
              private graphQLService: GraphQLService,
              private snackBarService: SnackBarService,
              public rightSidebarWaitScreenService: RightSidebarWaitScreenService,
              private store: Store<CollaborationState>,
              private securityStore: Store<SecurityState>) {
    store.pipe(select(getCurrentChatConversation)).subscribe(currentChatConversation => {
      this.currentChatConversation = currentChatConversation;
    });
    store.pipe(select(getConversationListInfo)).subscribe(conversationListInfo => {
      if (!conversationListInfo) {
        conversationListInfo = new ConversationListInfo();
      }
      this.contextId = conversationListInfo.contextId;
      this.listConversations = conversationListInfo.queryConversations || [];
      this.lowestModifiedDateForThisConversationList = new Date(conversationListInfo.lowestModifiedDate);
      if (!conversationListInfo.queryConversations || (conversationListInfo.queryConversations && conversationListInfo.queryConversations.length === 0)) {
        this.lowestModifiedDateForThisConversationList = new Date(2200, 3, 1);
        this.viewMore = true;
      } else {
        this.viewMore = false;
      }
      });


    securityStore.pipe(select(getUserInfo)).subscribe(currentUser => {
      this.currentUser = currentUser;
    });
  }


  createCustomerSupportChat$ = createEffect(() => this.actions$
    .pipe( ofType(CREATE_CUSTOMER_SUPPORT_CHAT_CONVERSATION) )
    .pipe(
      mergeMap((action: CreateCustomerSupportChatConversationAction) => {
        const startLiveSupportConversationCommand = new StartLiveSupportConversationCommand(
          '[COMPANY_ID]-chat',
          'Support Chat',
          0,
          'Conversation',
          []
        );
        startLiveSupportConversationCommand.scope = ContentScope.OrbitaxSupport;
        return this.commandService.executeCommand(startLiveSupportConversationCommand)
          .pipe(
            mergeMap(data => {
              return [new GetConversationChatAction(CollaborationConfig.CollaborationApiKey,
                CollaborationQuery.getChatConversationById(data.Payload.Id, GraphqlContentScope.getScope(startLiveSupportConversationCommand.scope)),
                '')
              ];
            }),
            catchError(error => of(new CreateChatConversationFailedAction(error)))
          );
      })
    ));


  createChat$ = createEffect(() => this.actions$
    .pipe( ofType(CREATE_CHAT_CONVERSATION) )
    .pipe(
      mergeMap((action: CreateChatConversationAction) => {
        // The Messages page has no sidebar to wait in (ITP-E-194-01).
        if (action.componentName !== MessagesPageHost) {
          this.rightSidebarWaitScreenService.showRightSidebarWaitScreen();
        }
        return this.commandService.executeCommand(action.command)
          .pipe(
            mergeMap(data => {
              this.fuseSplashScreenService.hide();
              const opened = new GetConversationChatAction(CollaborationConfig.CollaborationApiKey,
                CollaborationQuery.getChatConversationById(data.Payload.Id, GraphqlContentScope.getScope(action.command.scope)), action.componentName);
              // ITP-E-240-02: this tab hears its own CreateConversation as the command's answer, never as [OTHERS], so it brings
              // the new chat into its list itself, the way the creator's other tabs and the members' tabs do.
              const listed = this.readListForNewChat(data.Payload);
              return listed ? [opened, listed] : [opened];
            }),
            catchError(error => of(new CreateChatConversationFailedAction(error)))
          );
      })
    ));


  createConversationContext$ = createEffect(() => this.actions$
    .pipe( ofType(CREATE_CONVERSATION_CONTEXT) )
    .pipe(
      mergeMap((action: CreateConversationContextAction) => {
        this.fuseSplashScreenService.show();
        return this.commandService.executeCommand(action.command)
          .pipe(
            map(payload => {
              this.fuseSplashScreenService.hide();
              return new CreateConversationContextSuccessAction(payload);
            }),
            catchError(error => of(new CreateConversationContextFailedAction(error)))
          );
      })
    ));


  createConversationContextWithRelativePath$ = createEffect(() => this.actions$
    .pipe( ofType(CREATE_CONVERSATION_CONTEXT_WITH_RELATIVE_PATH) )
    .pipe(
      mergeMap((action: CreateConversationContextWithRelativePathAction) => {
        return this.commandService.executeCommand(action.command)
          .pipe(
            map(payload => {
              return new GetContextWithConversationListByPathAction(CollaborationConfig.CollaborationApiKey,
                CollaborationQuery.getContextWithConversationListByPath(action.command.relativeContextPath,
                  GraphqlContentScope.getScope(ContentScope.Default)), action.componentName);
            }),
            catchError(error => of(new CreateConversationContextWithRelativePathFailedAction(error)))
          );
      })
    ));


  createConversation$ = createEffect(() => this.actions$
    .pipe( ofType(CREATE_CONVERSATION) )
    .pipe(
      mergeMap((action: CreateConversationAction) => {
        return this.commandService.executeCommand(action.command)
          .pipe(
            mergeMap(payload => {
              this.fuseSplashScreenService.hide();
              if (action.sendMassageCommand) {
                return [new GetConversationChatAction(CollaborationConfig.CollaborationApiKey,
                  CollaborationQuery.getChatConversationById(payload.Payload.Id, GraphqlContentScope.getScope(action.command.scope)), action.componentName, action.sendMassageCommand)
                  ,
                  new GetContextByPathAction(CollaborationConfig.CollaborationApiKey,
                    CollaborationQuery.getContextWithConversationListByPath(action.command.replativeContextPath, GraphqlContentScope.getScope(action.command.scope)), '')];
              } else {
                return [new GetConversationChatAction(CollaborationConfig.CollaborationApiKey,
                  CollaborationQuery.getChatConversationById(payload.Payload.Id, GraphqlContentScope.getScope(action.command.scope)), action.componentName),
                  new GetContextByPathAction(CollaborationConfig.CollaborationApiKey,
                    CollaborationQuery.getContextWithConversationListByPath(action.command.replativeContextPath, GraphqlContentScope.getScope(action.command.scope)), '')
                ];
              }
            }),
            catchError(error => of(new CreateConversationFailedAction(error)))
          );
      })
    ));


  sendMessage$ = createEffect(() => this.actions$
    .pipe( ofType(SEND_MESSAGE) )
    .pipe(
      mergeMap((action: SendMessageAction) => {
        return this.commandService.executeCommand(action.command)
          .pipe(
            mergeMap(payload => {
              const next = action.getChat
                ? new GetConversationChatAction(CollaborationConfig.CollaborationApiKey,
                  CollaborationQuery.getChatConversationById(action.command.conversationId, GraphqlContentScope.getScope(action.command.scope)),
                  action.componentName || ConversationComponentName)
                : new SendMessageSuccessAction(payload.Payload);
              // ITP-E-249-08: every send the server took is confirmed, ahead of what follows it. The sender's list row shows the
              // message at once (the reducer): with `getChat` nothing else would, as this tab hears the message's frame as the
              // command's answer. A voice message's composer waits on its own message id (ITP-E-249-02).
              return [new SendMessageConfirmedAction(action.command as SendMessageCommand, payload.Payload), next];
            }),
            // ITP-E-249-02: the failure names its send, so a voice message's composer keeps the clip and says why.
            catchError(error => of(new SendMessageFailedAction(error, action.command)))
          );
      })
    ));


  deleteConversationMessage$ = createEffect(() => this.actions$
    .pipe( ofType(DELETE_CONVERSATION_MESSAGE) )
    .pipe(
      mergeMap((action: DeleteConversationMessageAction) => {
        return this.commandService.executeCommand(action.command)
          .pipe(
            map(payload =>
              new DeleteConversationMessageSuccessAction(payload)
            ),
            catchError(error => {
              // ITP-E-207-08: a refused delete for everyone says why ("You can only delete a message for everyone within 60 minutes").
              this.snackBarService.showError(refusalText(error, 'Message deletion failed.'));
              return of(new DeleteConversationMessageFailedAction(error));
            })
          );
      })
    ));

  editConversationMessage$ = createEffect(() => this.actions$
    .pipe( ofType(EDIT_CONVERSATION_MESSAGE) )
    .pipe(
      mergeMap((action: EditConversationMessageAction) => {
        return this.commandService.executeCommand(action.command)
          .pipe(
            map(payload =>
              new EditConversationMessageSuccessAction(payload)
            ),
            catchError(error => {
              // ITP-E-207-08: a refused edit says why ("You can only edit a message within 15 minutes").
              this.snackBarService.showError(refusalText(error, 'Message edit failed.'));
              return of(new EditConversationMessageFailedAction(error));
            })
          );
      })
    ));


  getChatConversation$ = createEffect(() => this.actions$
    .pipe( ofType(GET_CONVERSATION_CHAT) )
    .pipe(
      mergeMap((action: GetConversationChatAction) => {
        return this.graphQLService.executePostRequest(action.apiKey, action.query)
          .pipe(
            mergeMap((payload: any) =>
/*              this.commandService.executeCommand(new MessageReadCommand(payload.data.chatById ? payload.data.chatById.id : payload.data.supportChatById.id, true))
                .pipe(
                  mergeMap((messageReadPayload: any) =>
                    [
                      new GetConversationChatSuccessAction(payload.data, action.componentName),
                      new IncreaseQueryProviderCallCounterFromCollaborationAction()
                    ]
                  ),
                  catchError(error => of(new GetConversationChatFailedAction(error)))
                )*/
                [
                  new GetConversationChatSuccessAction(payload.data, action.componentName, action.lastSequence),
                  new IncreaseQueryProviderCallCounterFromCollaborationAction()
                ]
            ),
            catchError(error => of(new GetConversationChatFailedAction(error, action.lastSequence)))
          );
      })
    ));



  getConversation$ = createEffect(() => this.actions$
    .pipe( ofType(GET_CONVERSATION) )
    .pipe(
      mergeMap((action: GetConversationAction) => {
        return this.graphQLService.executePostRequest(action.apiKey, action.query)
          .pipe(
            mergeMap((payload: any) => {
                if (action.sendMassageCommand) {
                  return [new GetConversationSuccessAction(payload.data, action.componentName), new SendMessageAction(action.sendMassageCommand)];
                } else {
                  return [new GetConversationSuccessAction(payload.data, action.componentName)];
                }
              }
            ),
            catchError(error => of(new GetConversationFailedAction(error)))
          );
      })
    ));



  chatConversationSuccess$ = createEffect(() => this.actions$
    .pipe( ofType(GET_CONVERSATION_CHAT_SUCCESS) )
    .pipe(
      map((action: GetConversationChatSuccessAction) => {
          // A chat fetched for the Messages page stays on the page (ITP-E-194-01).
          if (action.componentName && action.componentName !== MessagesPageHost) {
            // console.log(`Navigating to ${action.componentName}`);
            return new SetCurrentRightSideComponentAction({
              component: action.componentName,
              data: {},
              componentInfo: null
            });
          } else {
            return new DoNothingAction();
          }
        }
      )
    ));


  newMessageNotification$ = createEffect(() => this.actions$
    .pipe( ofType(NEW_MESSAGE_NOTIFICATION) )
    .pipe(
      mergeMap((action: NewMessageNotificationAction) => {
        let actions: Array<any> = [];
        actions.push(new IncreaseQueryProviderCallCounterFromCollaborationAction());
          if (action && action.payload && action.payload.Payload && action.payload.Payload.NewRolesAdded && action.payload.Payload.ConversationId && this.currentChatConversation && action.payload.Payload.ConversationId === this.currentChatConversation.id) {
            actions.push(new GetConversationChatAction(CollaborationConfig.CollaborationApiKey,
              CollaborationQuery.getChatConversationById(action.payload.Payload.ConversationId, GraphqlContentScope.getScope(action.payload.Payload.ContentScope)),
              ''));
          }
        if (this.contextId &&  action && action.payload && action.payload.Payload && action.payload.Payload.ParentId && action.payload.Payload.ParentId === this.contextId) {
          actions.push(this.readListAgain());
        }
        return actions;
        }
      )
    ));
  /**
   * ITP-E-240-04: the reader's own message moves its row in the list at once (the reducer). A message into a chat of the
   * list's context that the list does not hold (one older than its pages, one just created) reads the list again, once per
   * burst, so the chat comes in at the top; a message from someone else already does (`newMessageNotification$`).
   * ITP-E-249-08: every send is confirmed, and one that reads no chat again succeeds as well; the burst's one read covers both.
   */
  sentIntoUnlistedChat$ = createEffect(() => this.actions$
    .pipe(ofType(SEND_MESSAGE_CONFIRMED, SEND_MESSAGE_SUCCESS))
    .pipe(
      map((action: SendMessageConfirmedAction | SendMessageSuccessAction) => action.payload),
      filter(payload => !!this.contextId && !!payload && payload.ParentId === this.contextId
        && !listHolds(this.listConversations, payload.ConversationId)),
      debounceTime(CollaborationEffects.listRereadDebounceMs),
      map(() => this.readListAgain())
    ));

  // @Effect()
  // liveSupportJoined$ = this.actions$
  //   .pipe( ofType(LIVE_SUPPORT_JOINED_NOTIFICATION) )
  //   .pipe(
  //     map((action) =>  {
  //       this.snackBarService.showInformation('An agent joined.');
  //       return new DoNothingAction();
  //       }
  //     )
  //   );

  startLiveSupportConversation$ = createEffect(() => this.actions$
    .pipe( ofType(START_LIVE_SUPPORT_CONVERSATION) )
    .pipe(
      map((action) => new IncreaseQueryProviderCallCounterFromCollaborationAction()
      )
    ));

  conversationSuccess$ = createEffect(() => this.actions$
    .pipe( ofType(GET_CONVERSATION_SUCCESS) )
    .pipe(
      map((action: GetConversationSuccessAction) => {
          // console.log(`Navigating to ${action.componentName}`);
          return new SetCurrentRightSideComponentAction({
            component: action.componentName,
            data: {isFold: false},
            componentInfo: this.getComponentInfo(action.componentName)
          });
        }
      )
    ));


  getChatConversationList$ = createEffect(() => this.actions$
    .pipe( ofType(GET_CONVERSATION_LIST) )
    .pipe(
      mergeMap((action: GetConversationListAction) => {
        return this.graphQLService.executePostRequest(action.apiKey, action.query)
          .pipe(
            map((payload: any) =>
              new GetConversationListSuccessAction(payload.data, action.queryContainsModifiedDate)
            ),
            catchError(error => of(new GetConversationListFailedAction(error)))
          );
      })
    ));


  getCollaborationAvatar$ = createEffect(() => this.actions$
    .pipe( ofType(GET_COLLABORATION_AVATAR) )
    .pipe(
      mergeMap((action: GetCollaborationAvatarAction) => {
        return this.collaborationService.getCollaborationAvatar(action.email, action.name, action.avatarSize)
          .pipe(
            map(avatarString =>
              new GetCollaborationAvatarSuccessAction(action.email, avatarString)
            ),
            catchError(error => of(new GetCollaborationAvatarFailedAction(error)))
          );
      })
    ));


  pickupLiveSupportConversation$ = createEffect(() => this.actions$
    .pipe( ofType(PICK_UP_LIVE_SUPPORT_CONVERSATION) )
    .pipe(
      mergeMap((action: PickupLiveSupportConversationAction) => {
        return this.commandService.executeCommand(action.command)
          .pipe(
            map(data => {
                return new GetConversationChatAction(CollaborationConfig.CollaborationApiKey,
                  CollaborationQuery.getSupportChatConversationById(data.Payload.Id, 'ORBITAX_SUPPORT'), action.componentName);
              }
            ),
            catchError(error => of(new CreateConversationFailedAction(error)))
          );
      })
    ));



getContext$ = createEffect(() => this.actions$
  .pipe( ofType(GET_CONTEXT_BY_PATH) )
  .pipe(
    mergeMap((action: GetContextByPathAction) => {
      return this.graphQLService.executePostRequest(action.apiKey, action.query)
        .pipe(
          map((payload: any) => {
              return new GetContextByPathSuccessAction(payload.data, action.componentName);
            }
          ),
          catchError(error => of(new GetContextByPathFailedAction(error)))
        );
    })
  ));


  getContextWithConversationList$ = createEffect(() => this.actions$
    .pipe( ofType(GET_CONTEXT_WITH_CONVERSATION_LIST_BY_PATH) )
    .pipe(
      mergeMap((action: GetContextWithConversationListByPathAction) => {
        return this.graphQLService.executePostRequest(action.apiKey, action.query)
          .pipe(
            mergeMap((payload: any) =>
              {
                let chat = new ConversationListInfo();
                if (payload && payload.data && payload.data.context && payload.data.context.queryConversationList) {
                  chat = payload.data.context.queryConversationList;
                }
                return [new GetContextByPathSuccessAction(payload.data, action.componentName), new GetConversationListSuccessAction({'chat': chat}, false)];
              }
            ),
            catchError(error => of(new GetContextWithConversationListByPathFailedAction(error)))
          );
      })
    ));



  contextByPathSuccess$ = createEffect(() => this.actions$
    .pipe( ofType(GET_CONTEXT_BY_PATH_SUCCESS) )
    .pipe(
      map((action: GetContextByPathSuccessAction) => {
          // console.log(`Navigating to ${action.componentName}`);
        if (!action.componentName) {
          return new DoNothingAction();
        }
          return new SetCurrentRightSideComponentAction({
            component: action.componentName,
            data: {},
            componentInfo: this.getComponentInfo(action.componentName)
          });

        }
      )
    ));


  messageRead$ = createEffect(() => this.actions$
    .pipe( ofType(MESSAGE_READ) )
    .pipe(
      mergeMap((action: MessageReadAction) => {
        return this.commandService.executeCommand(action.command)
          .pipe(
            mergeMap((payload: any) =>
              [new MessageReadSuccessAction(payload),
                new IncreaseQueryProviderCallCounterFromCollaborationAction()]
            ),
            catchError(error => of(new MessageReadFailedAction(error)))
          );
      })
    ));


  changeTitle$ = createEffect(() => this.actions$
    .pipe( ofType(CHANGE_CONVERSATION_TITLE) )
    .pipe(
      mergeMap((action: ChangeConversationTitleAction) => {
        return this.commandService.executeCommand(action.command)
          .pipe(
            map((payload: any) => {
                if (action.nextAction === 'conversation-list' && this.contextId) {
                  return this.readListAgain();
                } else {
                  return new ChangeConversationTitleSuccessAction(payload.Payload);
                }
              }
            ),
            catchError(error => of(new MessageReadFailedAction(error)))
          );
      })
    ));


  deleteConversation$ = createEffect(() => this.actions$
    .pipe( ofType(DELETE_CONVERSATION) )
    .pipe(
      mergeMap((action: DeleteConversationAction) => {
        return this.commandService.executeCommand(action.command)
          .pipe(
            map((payload: any) => {
                if (action.nextAction === MessagesPageHost) {
                  // The Messages page refreshes its list and clears its own selection (ITP-E-194-01).
                  return new GetConversationListAction(CollaborationConfig.CollaborationApiKey, CollaborationQuery.getConversationList());
                }
                if (action.nextAction === 'conversation-list' && this.contextId) {
                  return new GetConversationListAction(CollaborationConfig.CollaborationApiKey,
                    CollaborationQuery.getConversationListByContextId(this.contextId));
                } else {
                  return new SetCurrentRightSideComponentAction({
                    component: ConversationListComponentName,
                    data: {},
                    componentInfo: null
                  });
                }
              }
            ),
            catchError(error => of(new DeleteConversationFailedAction(error)))
          );
      })
    ));


  leaveConversation$ = createEffect(() => this.actions$
    .pipe( ofType(LEAVE_CONVERSATION) )
    .pipe(
      mergeMap((action: LeaveConversationAction) => {
        const onPage = action.nextAction === MessagesPageHost;
        return this.commandService.executeCommand(action.command)
          .pipe(
            mergeMap((payload: any) => {
              if (onPage) {
                // The Messages page keeps the conversation open, read-only (ITP-E-204-05): its list refreshes and the
                // conversation is read again in place, so the thread and Details show that the reader left.
                return [
                  new GetConversationListAction(CollaborationConfig.CollaborationApiKey, CollaborationQuery.getConversationList()),
                  this.rereadConversation(action.command.conversationId, MessagesPageHost)
                ];
              }
              if (action.nextAction === 'conversation-list' && this.contextId) {
                return [new GetConversationListAction(CollaborationConfig.CollaborationApiKey,
                  CollaborationQuery.getConversationListByContextId(this.contextId))];
              } else {
                  return [new SetCurrentRightSideComponentAction({
                    component: ConversationListComponentName,
                    data: {},
                    componentInfo: null
                  })];
                }
              }
            ),
            // A failed Leave (the only-admin rule on the server, a lost connection): the command layer has shown the
            // server's message. The list marked the conversation left ahead of the answer, and the thread still shows
            // it waiting, so both are read again and the reader never sees "left" while still in (ITP-E-204-05).
            catchError(error => {
              const conversationId = action.command.conversationId;
              const rereads: Action[] = [new GetConversationListAction(CollaborationConfig.CollaborationApiKey,
                this.contextId && !onPage
                  ? CollaborationQuery.getConversationListByContextId(this.contextId)
                  : CollaborationQuery.getConversationList())];
              if (this.currentChatConversation?.id === conversationId) {
                rereads.push(this.rereadConversation(conversationId, onPage ? MessagesPageHost : ''));
              }
              return from([new LeaveConversationFailedAction(error), ...rereads]);
            })
          );
      })
    ));


  deleteConversationList$ = createEffect(() => this.actions$
    .pipe( ofType(DELETE_CONVERSATION_LIST) )
    .pipe(
      mergeMap((action: DeleteConversationListAction) => {
        return this.commandService.executeCommand(action.command)
          .pipe(
            map((payload: any) =>
              new GetConversationListAction(CollaborationConfig.CollaborationApiKey,
                CollaborationQuery.getConversationList())
            ),
            catchError(error => of(new DeleteConversationListFailedAction(error)))
          );
      })
    ));



  manageParticipants$ = createEffect(() => this.actions$
    .pipe( ofType(MANAGE_PARTICIPANTS) )
    .pipe(
      mergeMap((action: ManageParticipantsAction) => {
        return this.commandService.executeCommand(action.command)
          .pipe(
            map((payload: any) => {
              if (action.nextAction === 'conversation-list' && this.contextId) {
                return this.readListAgain();
              } else {
                // On the Messages page the re-fetched chat stays on the page (ITP-E-194-01).
                return new GetConversationChatAction(CollaborationConfig.CollaborationApiKey,
                  CollaborationQuery.getChatConversationById(payload.Payload.Id, GraphqlContentScope.getScope(action.command.scope)),
                  action.nextAction === MessagesPageHost ? MessagesPageHost : ConversationComponentName);
              }
            }),
            // A refused change (ITP-E-204-07): the command layer shows the server's message. The thread changed its roles
            // in place before sending, so the conversation is read again and the old roles come back.
            catchError(error => {
              const conversationId = action.command.conversationId;
              return this.currentChatConversation?.id === conversationId
                ? from([new ManageParticipantsFailedAction(error),
                  this.rereadConversation(conversationId, action.nextAction === MessagesPageHost ? MessagesPageHost : '')])
                : of(new ManageParticipantsFailedAction(error));
            })
          );
      })
    ));

  othersManageParticipants$ = createEffect(() => this.actions$
    .pipe( ofType(OTHERS_MANAGE_PARTICIPANTS) )
    .pipe(
      mergeMap((action: ManageParticipantsNotificationAction) => {
          let actions: Array<any> = [];
          actions.push(new IncreaseQueryProviderCallCounterFromCollaborationAction());
        if (action && action.payload && action.payload.payload && action.payload.payload.id && this.currentChatConversation && action.payload.payload.id === this.currentChatConversation.id) {
          actions.push(new GetConversationChatAction(CollaborationConfig.CollaborationApiKey,
            CollaborationQuery.getChatConversationById(action.payload.payload.id, GraphqlContentScope.getScope(action.payload.payload.contentScope)),
            ''));
        }
        if (this.contextId &&  action && action.payload && action.payload.payload && action.payload.payload.parentId && action.payload.payload.parentId === this.contextId) {
          actions.push(this.readListAgain());
        }
        return actions;
      }
      )
    ));

  othersLeaveConversation$ = createEffect(() => this.actions$
    .pipe( ofType(OTHERS_LEAVE_CONVERSATION) )
    .pipe(
      mergeMap((action: OthersLeaveConversationNotificationAction) => {
        let actions: Array<any> = [];
        actions.push(new IncreaseQueryProviderCallCounterFromCollaborationAction());
        if (action && action.payload && action.payload.Payload && action.payload.Payload.ConversationId && this.currentChatConversation && action.payload.Payload.ConversationId === this.currentChatConversation.id) {
          actions.push(new GetConversationChatAction(CollaborationConfig.CollaborationApiKey,
            CollaborationQuery.getChatConversationById(action.payload.Payload.ConversationId, GraphqlContentScope.getScope(action.payload.Payload.ContentScope)),
            ''));
        }
        if (this.contextId &&  action && action.payload && action.payload.Payload && action.payload.Payload.ParentId && action.payload.Payload.ParentId === this.contextId) {
          actions.push(this.readListAgain());
        }
        return actions;
      }
      )
    ));

  othersCreateConversation$ = createEffect(() => this.actions$
    .pipe( ofType(OTHERS_CREATE_CONVERSATION) )
    .pipe(
      mergeMap((action: OthersCreateConversationNotificationAction) => {
        const listed = this.readListForNewChat(action && action.payload && action.payload.Payload);
        return listed ? [listed] : [];
        }
      )
    ));

  /**
   * ITP-E-240-02: a chat just created comes into the loaded list one way, in the tab that created it and in every tab that
   * hears of it: when it is a chat of the list's context that the list does not hold yet, the list is read again through its
   * query and the answer takes its place in the list's one order (ITP-E-240-04). The Messages page's list and the Chat
   * panel's are the company's chats, the context every chat created from them is in. Null when there is nothing to bring in.
   */
  private readListForNewChat(conversation: any): GetConversationListAction | null {
    return conversation && conversation.Id && this.contextId && conversation.ParentId === this.contextId
      && !listHolds(this.listConversations, conversation.Id)
      ? this.readListAgain()
      : null;
  }

  /** The loaded list read again through its query: its first page down to the lowest one loaded, and any chat newer. */
  private readListAgain(): GetConversationListAction {
    return new GetConversationListAction(CollaborationConfig.CollaborationApiKey,
      CollaborationQuery.getConversationListByContextId(this.contextId, this.lowestModifiedDateForThisConversationList, this.viewMore));
  }

  /** Reads a conversation again after a Leave or a membership change, in its own scope when it is the current one. */
  private rereadConversation(conversationId: string, componentName: string): GetConversationChatAction {
    const scope = this.currentChatConversation?.id === conversationId && this.currentChatConversation.scope
      ? this.currentChatConversation.scope
      : GraphqlContentScope.getScope(ContentScope.Company);
    return new GetConversationChatAction(CollaborationConfig.CollaborationApiKey,
      CollaborationQuery.getChatConversationById(conversationId, scope), componentName);
  }

  getComponentInfo(componentName: any) {
    if (componentName === 'FieldConversationComponent' || componentName === 'CollaborationConversationListComponent') {
      return {id: 'CollaborationConversationListComponent'};
    } else {
      return null;
    }
  }
}
